/**
 * AuthCheckPanel — G002 JWT 인증 검증 데모
 * R12 #331
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showcaseApi } from '../api/showcaseApi';
import type { AuthCheckResponse } from '../types/showcase';

function getStoredToken(): string {
  return (
    localStorage.getItem('access_token') ??
    sessionStorage.getItem('access_token') ??
    localStorage.getItem('accessToken') ??
    sessionStorage.getItem('accessToken') ??
    ''
  );
}

export function AuthCheckPanel() {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AuthCheckResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  function handleUseCurrentToken() {
    setToken(getStoredToken());
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await showcaseApi.authCheck({ token: token.trim() || null });
      setResult(res);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message ?? 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  const isPass = result?.decision === 'PASS';

  return (
    <section aria-labelledby="auth-check-panel-title">
      <h2
        id="auth-check-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.authCheck.title')}
      </h2>
      <p
        style={{
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-sm)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        G002 — eraf-gateway / eraf-security
      </p>

      <form onSubmit={handleSubmit} noValidate>
        <label
          htmlFor="auth-token"
          style={{
            display: 'block',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--spacing-1)',
          }}
        >
          {t('showcase.authCheck.tokenLabel')}
        </label>
        <textarea
          id="auth-token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          rows={4}
          placeholder="Bearer eyJhbGciOiJIUzM4NiJ9..."
          aria-label={t('showcase.authCheck.tokenLabel')}
          style={{
            width: '100%',
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            padding: 'var(--spacing-2) var(--spacing-3)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-surface-elevated)',
            color: 'var(--color-text-primary)',
            resize: 'vertical',
            boxSizing: 'border-box',
            marginBottom: 'var(--spacing-2)',
          }}
        />

        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-2)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <button
            type="button"
            onClick={handleUseCurrentToken}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
          >
            {t('showcase.authCheck.useCurrentToken')}
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: loading ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
              color: loading ? 'var(--color-text-muted)' : 'var(--color-text-on-brand)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontWeight: 700,
              fontSize: 'var(--font-size-sm)',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? '...' : t('showcase.authCheck.verifyButton')}
          </button>
        </div>
      </form>

      {/* Error */}
      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--spacing-3)',
            background: 'var(--color-status-error-bg)',
            border: '1px solid var(--color-status-error)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-status-error)',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <div
          style={{
            border: `1px solid ${isPass ? 'var(--color-status-success)' : 'var(--color-status-error)'}`,
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {/* Decision badge */}
          <div
            style={{
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: isPass ? 'var(--color-status-success-bg)' : 'var(--color-status-error-bg)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-3)',
            }}
          >
            <span
              role="status"
              aria-label={`Decision: ${result.decision}`}
              style={{
                fontFamily: 'var(--font-family-mono)',
                fontWeight: 700,
                fontSize: 'var(--font-size-lg)',
                color: isPass ? 'var(--color-status-success)' : 'var(--color-status-error)',
              }}
            >
              {isPass ? t('showcase.authCheck.decision.PASS') : t('showcase.authCheck.decision.REJECT')}
            </span>
            {result.reason && (
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                {t('showcase.authCheck.reasonLabel')}: {result.reason}
              </span>
            )}
          </div>

          {/* Claims table */}
          {result.claims && (
            <div style={{ padding: 'var(--spacing-3) var(--spacing-4)' }}>
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  color: 'var(--color-text-secondary)',
                  marginBottom: 'var(--spacing-2)',
                  marginTop: 0,
                }}
              >
                {t('showcase.authCheck.claimsLabel')}
              </p>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-xs)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                <tbody>
                  {[
                    ['sub', result.claims.sub],
                    ['exp', new Date(result.claims.exp * 1000).toISOString()],
                    ['iat', new Date(result.claims.iat * 1000).toISOString()],
                    ['jti', result.claims.jti],
                    ['roles', result.claims.roles.join(', ')],
                    ['permissions', result.claims.permissions.slice(0, 5).join(', ') + (result.claims.permissions.length > 5 ? ' ...' : '')],
                  ].map(([k, v]) => (
                    <tr key={k}>
                      <td
                        style={{
                          padding: 'var(--spacing-1) var(--spacing-2)',
                          color: 'var(--color-text-muted)',
                          fontWeight: 600,
                          borderBottom: '1px solid var(--color-surface-border)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {k}
                      </td>
                      <td
                        style={{
                          padding: 'var(--spacing-1) var(--spacing-2)',
                          color: 'var(--color-text-primary)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          wordBreak: 'break-all',
                        }}
                      >
                        {v}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Cross-link */}
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        <a
          href="/dev/token-inspector"
          style={{
            color: 'var(--color-brand-primary)',
            textDecoration: 'underline',
          }}
        >
          {t('showcase.authCheck.inspectorLink')} →
        </a>
      </p>
    </section>
  );
}
